import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { motion } from "framer-motion";
import { isDarkMode } from "../../utils/constants";
import { ChartCanvas } from "./Charting";
import ChartLayerControls from "./Charting/ChartLayerControls";
import { useChartDeliveryReceipt } from "../ChatWorkspace/useChartDeliveryReceipt";
import WorkspaceChartLoadingState from "../ChatWorkspace/WorkspaceChartLoadingState";
import { workspaceChartAccessCopy } from "../ChatWorkspace/workspaceChartAccess";

const MainChart = memo(
  ({
    idx,
    containerHeightOverride = null,
    chartMessageThread,
    isLastMessage,
    freeUnlimitedOnly,
    hasShownFreeUnlimitedMessage,
    isMobile,
    chartDataPresent,
    hasRendered,
    setHasRendered,
    selectedTickerId,
    tradeOverlayKey,
    agenticChartState,
    agenticOverlayKey,
    showIndicators,
    onToggleIndicators,
    selectedScale,
    chartViewType,
    chartTickerPrices,
    chartAssetType,
    extendedHoursQuote,
    livePriceQuote,
    priceMarkers,
    chartConfigKey,
    sharedRangeBus,
    mainChartInstance,
    setMainChartInstance,
    chartCanvasStyle,
    mainChartAnim,
    handleExportCharts,
    applyMainZoom,
    startZoomHold,
    stopZoomHold,
    zoomHoldingRef,
    userTimezone,
    conversationThreadUuid,
    chartUnavailable = false,
    chartAccessBlock = null,
    onRetryChart,
  }) => {
    const navigate = useNavigate();
    const responseDelivery = chartMessageThread?.workspaceChartDelivery;
    const isWorkspaceAnswerChart = responseDelivery?.destination === "response";
    const barsLoaded = chartTickerPrices?.length > 1;
    // Whether the workspace answer chart could paint from bars already in the
    // page when it was assigned (warm) or had to fetch them (cold).
    const receiptCacheRef = useRef({ key: null, state: null });
    const receiptCacheKey = `${chartMessageThread?.id || ""}|${selectedTickerId || ""}|${selectedScale || ""}`;
    if (receiptCacheRef.current.key !== receiptCacheKey) {
      receiptCacheRef.current = { key: receiptCacheKey, state: barsLoaded ? "warm" : "cold" };
    }
    const deliveryReceipt = useChartDeliveryReceipt({
      enabled: isWorkspaceAnswerChart,
      threadUuid: conversationThreadUuid,
      messageId: chartMessageThread?.id,
      sourceMessageId: responseDelivery?.sourceMessageId,
      tickerId: selectedTickerId, scale: selectedScale, destination: "response",
      loaded: barsLoaded,
      unavailable: chartUnavailable,
      withheld: Boolean(chartAccessBlock),
      answerMessageId:
        chartMessageThread?.workspaceResponseMessageId ||
        chartMessageThread?.workspaceAnswerChartParentId ||
        chartMessageThread?.id,
      chartIndex: responseDelivery?.chartIndex ?? null,
      chartCount: responseDelivery?.chartCount ?? null,
      cacheState: receiptCacheRef.current.state,
    });
    const recordRendered = deliveryReceipt.onRendered;
    const handleRendered = useCallback((value) => {
      setHasRendered(value);
      recordRendered(value);
    }, [recordRendered, setHasRendered]);
    const containerHeight = useMemo(() => {
      if (!isLastMessage) return "420px";
      if (!freeUnlimitedOnly) return "420px";
      if (hasShownFreeUnlimitedMessage) {
        return isMobile ? "380px" : "420px";
      }
      return isMobile ? "474px" : "500px";
    }, [isLastMessage, freeUnlimitedOnly, hasShownFreeUnlimitedMessage, isMobile]);

    const selectedTickerLabel = useMemo(() => {
      const idStr = selectedTickerId != null ? String(selectedTickerId) : null;

      if (
        idStr &&
        Array.isArray(chartMessageThread?.answeredTickerMap) &&
        chartMessageThread.answeredTickerMap.length > 0
      ) {
        const match = chartMessageThread.answeredTickerMap.find(
          (entry) => entry && String(entry.id) === idStr,
        );
        if (match?.ticker) return String(match.ticker);
      }

      const ids = Array.isArray(chartMessageThread?.answeredTickerIds)
        ? chartMessageThread.answeredTickerIds.map((id) => String(id))
        : [];
      const tickers = Array.isArray(chartMessageThread?.ticker)
        ? chartMessageThread.ticker
        : chartMessageThread?.ticker != null
          ? [chartMessageThread.ticker]
          : [];

      if (idStr && ids.length > 0 && tickers.length > 0) {
        const idx = ids.findIndex((id) => id === idStr);
        if (idx >= 0) return String(tickers[idx] || tickers[0] || "");
      }

      if (tickers.length > 0) return String(tickers[0] || "");
      return "";
    }, [
      selectedTickerId,
      chartMessageThread?.answeredTickerMap,
      chartMessageThread?.answeredTickerIds,
      chartMessageThread?.ticker,
    ]);
    const prevAnimKeyRef = useRef(null);

    useEffect(() => {
      if (!chartDataPresent) return;
      // Fade only for user-driven chart changes — ticker switch, scale
      // switch, view-type switch. Anything that populates from the HTTP
      // response (chartConfigKey, agenticOverlayKey) is excluded so the
      // chart doesn't flash when bars/overlay metadata fill in.
      const nextKey = `${selectedTickerId ?? ""}|${selectedScale}|${chartViewType}`;
      if (prevAnimKeyRef.current === nextKey) return;
      prevAnimKeyRef.current = nextKey;
      mainChartAnim.set({ opacity: 0 });
      mainChartAnim.start({
        opacity: 1,
        transition: { duration: 0.35, ease: "easeOut" },
      });
    }, [
      chartDataPresent,
      selectedTickerId,
      selectedScale,
      chartViewType,
      mainChartAnim,
    ]);

    if (!chartDataPresent) return null;

    const accessCopy = isWorkspaceAnswerChart
      ? workspaceChartAccessCopy({
        block: chartAccessBlock,
        tickerLabel: selectedTickerLabel,
        hasOtherCharts: responseDelivery?.chartCount > 1,
      })
      : null;

    return (
      <Box
        id={`chart-container-${idx}`}
        data-live-price-chart
        ref={deliveryReceipt.containerRef}
        data-chart-delivery-state={deliveryReceipt.status}
        data-chart-delivery-ticker-id={responseDelivery ? selectedTickerId : undefined}
        sx={{
          // Fixed height to prevent layout shifts while data loads.
          height: containerHeightOverride || containerHeight,
          width: "100%",
          position: "relative",
          borderRadius: "14px",
          padding: isMobile ? "8px 2px 8px 0" : "10px 2px 8px 0",
          background: isDarkMode
            ? "linear-gradient(180deg, #0f1115 0%, #0b0d10 100%)"
            : "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
          border: isDarkMode ? "1px solid #1f2937" : "1px solid #e5e7eb",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minWidth: "100%",
        }}
      >
        {accessCopy ? (
          <Box role="status" data-response-chart-withheld data-response-chart-withheld-status={chartAccessBlock.status} sx={{ position: "absolute", inset: 0, zIndex: 5, bgcolor: isDarkMode ? "#0f1115" : "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, px: 3, textAlign: "center" }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{accessCopy.title}</Box>
            <Box component="span" sx={{ fontSize: 13, opacity: 0.72 }}>{accessCopy.body}</Box>
            {accessCopy.actionLabel && chartAccessBlock.upgradeRoute ? (
              <Button onClick={() => navigate(chartAccessBlock.upgradeRoute)}>{accessCopy.actionLabel}</Button>
            ) : null}
          </Box>
        ) : isWorkspaceAnswerChart && chartUnavailable ? (
          <Box role="status" data-response-chart-unavailable sx={{ position: "absolute", inset: 0, zIndex: 5, bgcolor: isDarkMode ? "#0f1115" : "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, px: 3, textAlign: "center" }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{`Couldn't load ${selectedTickerLabel || "this chart"}`}</Box>
            <Box component="span" sx={{ fontSize: 13, opacity: 0.72 }}>
              {`${selectedTickerLabel || "Chart"} ${selectedScale} data didn't arrive. ${responseDelivery?.chartCount > 1 ? "Try again, or choose another chart above." : "Try again in a moment."}`}
            </Box>
            <Button onClick={onRetryChart}>Retry</Button>
          </Box>
        ) : isWorkspaceAnswerChart && !barsLoaded ? (
          // A stable panel with a truthful state while this chart's own bars
          // load; another instrument's candles are never shown meanwhile.
          <Box data-response-chart-loading sx={{ position: "absolute", inset: 0, zIndex: 4, display: "flex", bgcolor: isDarkMode ? "#0f1115" : "#f8fafc" }}>
            <WorkspaceChartLoadingState title={`Loading ${selectedTickerLabel || "chart"}`} />
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <motion.div
            initial={false}
            animate={mainChartAnim}
            style={{ width: "100%", height: "100%" }}
          >
            <ChartCanvas
              idx={idx}
              messageThread={chartMessageThread}
              chartConfigKey={chartConfigKey}
              hasRendered={responseDelivery ? deliveryReceipt.painted : hasRendered}
              setHasRendered={responseDelivery ? handleRendered : setHasRendered}
              selectedTickerId={selectedTickerId}
              tradeOverlayKey={tradeOverlayKey}
              agenticChartState={agenticChartState}
              agenticOverlayKey={agenticOverlayKey}
              showIndicators={showIndicators}
              selectedScale={selectedScale}
              chartViewType={chartViewType}
              tickerPrices={chartTickerPrices}
              assetType={chartAssetType}
              extendedHoursQuote={extendedHoursQuote}
              livePriceQuote={livePriceQuote}
              priceMarkers={priceMarkers}
              sharedRangeBus={sharedRangeBus}
              onChartReady={setMainChartInstance}
              chartDataPresent={chartDataPresent}
              canvasId={`myChart-${idx}`}
              canvasStyle={chartCanvasStyle}
              userTimezone={userTimezone}
            />
          </motion.div>
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: isMobile ? 8 : 10,
            left: isMobile ? 8 : 12,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          {selectedTickerLabel && (
            <Box
              component="span"
              sx={{
                fontSize: isMobile ? "15px" : "16.5px",
                fontWeight: 700,
                letterSpacing: "0.05em",
                color: isDarkMode ? "rgba(229,231,235,0.68)" : "rgba(17,24,39,0.62)",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              {selectedTickerLabel}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: isMobile ? 8 : 10,
            right: isMobile ? 64 : 72,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.35 : 0.5,
          }}
        >
          <ChartLayerControls
            showIndicators={showIndicators}
            onToggleIndicators={onToggleIndicators}
            isMobile={isMobile}
          />
          <Tooltip title="Screenshot Chart">
            <IconButton
              size="small"
              onClick={handleExportCharts}
              sx={{
                width: isMobile ? 28 : 30,
                height: isMobile ? 28 : 30,
                borderRadius: "9px",
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
                color: isDarkMode ? "#e5e7eb" : "#111827",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(255,255,255,0.14)"
                    : "rgba(0,0,0,0.12)",
                },
              }}
              aria-label="Save charts"
            >
              <CameraAltIcon
                sx={{
                  fontSize: isMobile ? 16 : 18,
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: isMobile ? 8 : 12,
            bottom: isMobile ? 8 : 12,
            display: "flex",
            gap: 1,
            zIndex: 2,
            backgroundColor: isDarkMode
              ? "rgba(15,17,22,0.7)"
              : "rgba(255,255,255,0.8)",
            borderRadius: "10px",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid rgba(0,0,0,0.08)",
            boxShadow: isDarkMode
              ? "0 4px 12px rgba(0,0,0,0.45)"
              : "0 6px 16px rgba(15,23,42,0.12)",
            padding: "4px 6px",
            alignItems: "center",
          }}
        >
          <Tooltip title="Reset price scale">
            <IconButton
              size="small"
              aria-label="Reset price scale"
              onClick={() => {
                mainChartInstance?.resetPriceScale?.();
              }}
              sx={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}
            >
              <AutorenewIcon
                sx={{
                  fontSize: isMobile ? 17 : 18,
                }}
              />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            aria-label="Zoom out"
            onClick={() => {
              if (zoomHoldingRef?.current) return;
              applyMainZoom?.(0.8);
            }}
            onPointerDown={startZoomHold?.(0.8, applyMainZoom)}
            onPointerUp={stopZoomHold}
            onPointerLeave={stopZoomHold}
            onPointerCancel={stopZoomHold}
            sx={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}
          >
            −
          </IconButton>
          <IconButton
            size="small"
            aria-label="Zoom in"
            onClick={() => {
              if (zoomHoldingRef?.current) return;
              applyMainZoom?.(1.25);
            }}
            onPointerDown={startZoomHold?.(1.25, applyMainZoom)}
            onPointerUp={stopZoomHold}
            onPointerLeave={stopZoomHold}
            onPointerCancel={stopZoomHold}
            sx={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}
          >
            +
          </IconButton>
        </Box>
      </Box>
    );
  },
  (prev, next) => {
    if (prev.idx !== next.idx) return false;
    if (prev.containerHeightOverride !== next.containerHeightOverride) return false;
    if (prev.chartMessageThread !== next.chartMessageThread) return false;
    if (prev.isLastMessage !== next.isLastMessage) return false;
    if (prev.freeUnlimitedOnly !== next.freeUnlimitedOnly) return false;
    if (prev.hasShownFreeUnlimitedMessage !== next.hasShownFreeUnlimitedMessage)
      return false;
    if (prev.isMobile !== next.isMobile) return false;
    if (prev.chartDataPresent !== next.chartDataPresent) return false;
    if (prev.hasRendered !== next.hasRendered) return false;
    if (prev.selectedTickerId !== next.selectedTickerId) return false;
    if (prev.showIndicators !== next.showIndicators) return false;
    if (prev.selectedScale !== next.selectedScale) return false;
    if (prev.chartViewType !== next.chartViewType) return false;
    if (prev.chartTickerPrices !== next.chartTickerPrices) return false;
    if (prev.chartAssetType !== next.chartAssetType) return false;
    if (prev.chartConfigKey !== next.chartConfigKey) return false;
    if (prev.sharedRangeBus !== next.sharedRangeBus) return false;
    if (prev.mainChartInstance !== next.mainChartInstance) return false;
    if (prev.chartCanvasStyle !== next.chartCanvasStyle) return false;
    if (prev.mainChartAnim !== next.mainChartAnim) return false;
    if (prev.handleExportCharts !== next.handleExportCharts) return false;
    if (prev.onToggleIndicators !== next.onToggleIndicators) return false;
    if (prev.applyMainZoom !== next.applyMainZoom) return false;
    if (prev.startZoomHold !== next.startZoomHold) return false;
    if (prev.stopZoomHold !== next.stopZoomHold) return false;
    if (prev.zoomHoldingRef !== next.zoomHoldingRef) return false;
    if (prev.userTimezone !== next.userTimezone) return false;
    if (prev.chartUnavailable !== next.chartUnavailable) return false;
    if (prev.chartAccessBlock !== next.chartAccessBlock) return false;
    return true;
  }
);

MainChart.displayName = "MainChart";

export default MainChart;
